// @ts-check
/**
 * BV Cart Page - Enhanced with AJAX updates, max quantity validation, and low stock warnings
 */

(function() {
  'use strict';

  const cartPage = document.querySelector('.bv-cart-page');
  const sectionId = cartPage?.getAttribute('data-section-id');
  // const quantitySelectorUpdateEventName = 'quantity-selector:update';
  const MAX_QTY_MESSAGE = (cartPage instanceof HTMLElement && cartPage.dataset.maxQtyMessage) ? cartPage.dataset.maxQtyMessage : 'Maximum allowed quantity reached';

  /**
   * Get max quantity for a cart item row (from data attributes).
   * @param {HTMLElement} itemElement
   * @returns {number | null}
   */
  function getMaxQuantity(itemElement) {
    if (!itemElement || !(itemElement instanceof HTMLElement)) return null;
    const inventoryQty = parseInt(itemElement.dataset.inventoryQuantity || '0', 10);
    const inventoryPolicy = itemElement.dataset.inventoryPolicy;
    const maxQty = itemElement.dataset.maxQuantity ? parseInt(itemElement.dataset.maxQuantity, 10) : null;
    if (inventoryPolicy === 'deny' && inventoryQty > 0) {
      if (maxQty === null) return inventoryQty;
      return Math.min(maxQty, inventoryQty);
    }
    return maxQty;
  }

  /**
   * Show max quantity message below the quantity selector for a cart item.
   * @param {HTMLElement} itemElement
   * @param {string} message
   */
  function showMaxQtyMessage(itemElement, message) {
    const messageEl = itemElement.querySelector('[data-max-qty-message]');
    if (!messageEl || !(messageEl instanceof HTMLElement)) return;
    messageEl.textContent = message;
    messageEl.style.display = 'block';
    messageEl.classList.add('bv-cart-item__max-qty-message--active');
    setTimeout(function() {
      messageEl.style.display = 'none';
      messageEl.classList.remove('bv-cart-item__max-qty-message--active');
    }, 5000);
  }

  // Get Horizon utilities from global scope
  // @ts-ignore
  const sectionRenderer = window.sectionRenderer || (window.Theme && window.Theme.sectionRenderer);
  // @ts-ignore
  const morphSection = window.morphSection || (window.Theme && window.Theme.morphSection);
  // @ts-ignore
  const ThemeEvents = window.ThemeEvents || (window.Theme && window.Theme.events);
  // @ts-ignore
  const CartUpdateEvent = window.CartUpdateEvent || (window.Theme && window.Theme.events && window.Theme.events.CartUpdateEvent);

  /**
   * Update section innerHTML but preserve cart-progress-bar nodes so the bar is not re-rendered (only JS updates it).
   * @param {HTMLElement} sectionElement
   * @param {string} newHtml
   */
  function applySectionHtmlPreservingProgressBar(sectionElement, newHtml) {
    const saved = Array.from(sectionElement.querySelectorAll('cart-progress-bar, .bv-cart-progress'));
    saved.forEach((el) => el.remove());
    sectionElement.innerHTML = newHtml;
    const newBars = sectionElement.querySelectorAll('cart-progress-bar, .bv-cart-progress');
    saved.forEach((savedEl, i) => {
      if (newBars[i]) newBars[i].replaceWith(savedEl);
    });
  }

  // When cart:update has no sections (e.g. free gift add/remove from bv-cart-progress), re-fetch this section so cart page updates in real time
  

  /**
   * Refresh +/- button disabled state on all quantity selectors (e.g. after section morph).
   */
  function refreshQuantitySelectorButtonStates() {
    if (!cartPage || !cartPage.isConnected) return;
    cartPage.querySelectorAll('cart-quantity-selector-component').forEach(function(el) {
      if (typeof el.updateButtonStates === 'function') {
        el.updateButtonStates();
      }
    });
  }

  // Remove buttons and quantity updates are now handled natively by Horizon's
  // component-cart-items.js (via #onRemoveClick and QuantitySelectorUpdateEvent).
  // No custom handlers needed here — they were conflicting with the native flow,
  // especially in the cart drawer where sectionId was null.

  // Refresh quantity selector button states after section morph (new DOM)
  document.addEventListener('cart:update', () => {
    if (cartPage && document.body.contains(cartPage)) {
      setTimeout(refreshQuantitySelectorButtonStates, 0);
    }
  });

  /* ===============================
     CART IMAGE PLACEHOLDER HANDLING
  ================================== */

  /**
   * Handle image load errors by replacing with placeholder
   * @param {HTMLImageElement} img - The image element that failed to load
   */
  function handleImageError(img) {
    if (!(img instanceof HTMLImageElement)) return;
    
    // Don't replace if already showing placeholder
    if (img.src.includes('no-image')) return;
    
    // Get placeholder URLs from data attributes or construct from asset path
    const placeholderSmall = img.dataset.placeholderSmall || '/assets/no-image-small.jpg';
    const placeholderLarge = img.dataset.placeholderLarge || '/assets/no-image-large.jpg';
    
    // Determine which placeholder to use based on viewport
    const isMobile = window.innerWidth <= 749;
    const placeholderSrc = isMobile ? placeholderSmall : placeholderLarge;
    
    // Replace with placeholder
    img.src = placeholderSrc;
    img.alt = img.alt || 'Product image not available';
    
    // Prevent infinite loop if placeholder also fails
    img.onerror = null;
  }

  /**
   * Initialize image error handlers for cart images
   */
  function initCartImageErrorHandlers() {
    // Handle existing images
    const cartImages = document.querySelectorAll('.bv-cart-item__image img');
    cartImages.forEach((img) => {
      if (img instanceof HTMLImageElement) {
        // Only add handler if image hasn't loaded yet or if it might fail
        if (!img.complete || img.naturalHeight === 0) {
          img.addEventListener('error', () => handleImageError(img), { once: true });
        }
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartImageErrorHandlers);
  } else {
    initCartImageErrorHandlers();
  }

  // Re-initialize when cart is updated
  document.addEventListener('cart:update', () => {
    setTimeout(initCartImageErrorHandlers, 200);
  });

  // Use MutationObserver to catch dynamically added images
  const cartImageObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            const images = node.querySelectorAll ? node.querySelectorAll('.bv-cart-item__image img') : [];
            images.forEach((img) => {
              if (img instanceof HTMLImageElement) {
                img.addEventListener('error', () => handleImageError(img), { once: true });
              }
            });
            
            // Also check if the node itself is an image
            if (node instanceof HTMLImageElement && node.closest('.bv-cart-item__image')) {
              node.addEventListener('error', () => handleImageError(node), { once: true });
            }
          }
        });
      }
    });
  });

  // Observe cart page for image additions
  function setupCartImageObserver() {
    const cartPage = document.querySelector('.bv-cart-page');
    
    if (cartPage instanceof HTMLElement) {
      cartImageObserver.observe(cartPage, {
        childList: true,
        subtree: true
      });
    }
  }

  // Set up observer when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupCartImageObserver);
  } else {
    setupCartImageObserver();
  }

  /* ===============================
     MINI CART MAIN TABS (Cart / Auctions)
  ============================== */
  /**
   * @param {HTMLElement} root
   */
  function initMiniCartTabs(root) {
    const tabs = Array.from(root.querySelectorAll("[data-bv-tab]"));
    const panels = Array.from(root.querySelectorAll("[data-bv-panel]"));
    if (!tabs.length || !panels.length) return;

    /**
     * @param {string} name
     */
    function activate(name) {
      tabs.forEach((btn) => {
        const isActive = btn.getAttribute("data-bv-tab") === name;
        btn.classList.toggle("is-active", isActive);
        btn.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      panels.forEach((panel) => {
        const isActive = panel.getAttribute("data-bv-panel") === name;
        panel.classList.toggle("is-active", isActive);
        panel.setAttribute("aria-hidden", isActive ? "false" : "true");
      });
    }

    tabs.forEach((btn) => {
      btn.addEventListener("click", () => {
        const tab = btn.getAttribute("data-bv-tab");
        if (tab === "cart" || tab === "auctions") activate(tab);
      });
    });

    activate("cart");
  }

  function bootTabs() {
    document.querySelectorAll(".bv-mini-cart").forEach(initMiniCartTabs);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootTabs);
  } else {
    bootTabs();
  }

  document.addEventListener("shopify:section:load", bootTabs);

})();
